import React, { useState, useEffect } from 'react';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Button from 'antd/lib/button';
import { retrieveQuote, getApplication } from '../api/applicationAPI';
import { navigateWithData } from '../navigation/navigationHistory';
import {AsYouType} from "libphonenumber-js";
import {DatePicker} from './fields/DatePicker';

export const RetrieveWidget = ({ zipCodeLabel, phoneNumberLabel, birthDateLabel, emailAddressLabel, buttonLabel, errorMessage, unavailableErrorMessage }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [zipCode, setZipCode] = useState('');
  const [quoteNumber, setQuoteNumber] = useState();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [{errorMsg, errorItems}, setErrorMessage] = useState({errorMsg: ''});
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const parsedErrorMessage = errorMessage ? errorMessage.replace(/<quoteNumber>/g, quoteNumber) : '';

  const unavailableDefault = 'Thank you for visiting our website.  We are currently undergoing maintenance and cannot process your request.  Please visit us again later or call us for assistance.';
  const unavailableMsg = unavailableErrorMessage || unavailableDefault;

  useEffect(() => {
    if ((zipCodeLabel ? zipCode : true)
        && (phoneNumberLabel ? phoneNumber : true)
        && (birthDateLabel ? birthDate : true)
        && (emailAddressLabel ? emailAddress : true)
        && quoteNumber) {
      return setIsDisabled(false);
    }
    return setIsDisabled(true);
  });

  const handleZipInput = (e) => {
    // only set zip code if it's a single number
    const zip = e.target.value;

    if (/^\d*$/.test(zip)) {
      setZipCode(zip);
    }
  };

  const handlePhoneInput = (e) => {
    let formattedPhone = e.target.value;
    if (formattedPhone.length >= 5) {
      formattedPhone = new AsYouType('US').input(formattedPhone);
    }
    setPhoneNumber(formattedPhone);
  };

  const handleDateInput = (e) => {
    setBirthDate(e);
  };

  const handleEmailInput = (e) => {
    setEmailAddress(e.target.value);
  };

  const handleQuoteInput = (e) => {
    setQuoteNumber(e.target.value);
  };

  const handleRetrieveClick = () => {
    if (quoteNumber === undefined || quoteNumber.length === 0) {
      setDisplayErrorMessage(true);
      setErrorMessage({errorMsg: "The Quote Number is required"});
      return;
    }

    setIsLoading(true);
    setIsDisabled(true);
    setDisplayErrorMessage(false);

    const request = {
      postalCode: zipCodeLabel ? zipCode.toString() : '',
      phoneNumber: phoneNumberLabel ? phoneNumber.toString() : '',
      emailAddress: emailAddressLabel ? emailAddress.toString() : '',
      quoteOrAppNumber: quoteNumber,
    };

    if (birthDateLabel && birthDate.length > 0) {
      request["birthDate"] = birthDate.toString();
    }

    retrieveQuote(request)
      .then((res) => {
        getApplication(res).then((app) => {
          navigateWithData('/quote-summary', app);
          setIsLoading(false);
          setIsDisabled(false);
          scrollTo(0, 0);
        });
      })
      .catch((err) => {
        const statusCode = (err.response ? err.response.status : 0);
        setDisplayErrorMessage(true);

        setIsDisabled(false);
        setIsLoading(false);
        scrollTo({
          top: 0,
          behavior: 'smooth',
        });

        if (statusCode === 403 && err.response.data.message === "Application has been processed into a policy") {
          return setErrorMessage({errorMsg: parsedErrorMessage});
        }

        if (statusCode === 403) {
          return navigateWithData('/quote-review', quoteNumber);
        }

        if (statusCode === 404) {
          return setErrorMessage({errorItems: [
              zipCode > 0 ? `Zip Code: ${zipCode}` : null,
              phoneNumber.length > 0 ? `Phone Number: ${phoneNumber}` : null,
              birthDate.length > 0 ? `Birth Date: ${birthDate}` : null,
              emailAddress.length > 0 ? `Email Address: ${emailAddress}` : null
          ]});
        }
        
        if (statusCode === 503) {
            return navigateWithData('/server-maintenance', quoteNumber);  // This is unlikely to happen
        }

        setErrorMessage({errorMsg: unavailableMsg});
      });
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          marginBottom: '16px',
        }}
      >
        <ErrorMessageField displayError={displayErrorMessage} errorMsg={errorMsg} errorItems={errorItems} quoteNumber={quoteNumber} />
      </div>
      <ZipField arialLabel={'Consumer zip code on quote'} label={zipCodeLabel} value={zipCode} onChange={handleZipInput} onPressEnter={handleRetrieveClick}/>
      <StringField arialLabel={'Consumer phone number on quote'} label={phoneNumberLabel} value={phoneNumber} onChange={handlePhoneInput} onPressEnter={handleRetrieveClick}/>
      <DateField arialLabel={'Birth Date of the consumer'} label={birthDateLabel} value={birthDate} onChange={handleDateInput}/>
      <StringField arialLabel={'Email Address of the consumer'} label={emailAddressLabel} value={emailAddress} onChange={handleEmailInput} onPressEnter={handleRetrieveClick}/>
      <div style={{ marginTop: '12px' }}>
        <div style={styles.textContainer}>
          <p style={styles.text}>Quote Number</p>
        </div>
        <Input aria-label={'Number of quote to retrieve'} onChange={handleQuoteInput} onPressEnter={handleRetrieveClick} />
      </div>
      <div style={styles.buttonContainer}>
        <div style={{ width: '70%' }}>
          <Button
            loading={isLoading}
            disabled={isDisabled}
            className="retrieve-button"
            type="primary"
            onClick={handleRetrieveClick}
          >
            {buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    textAlign: 'center',
    marginTop: '32px',
  },
  text: {
    margin: 0,
  },
  textContainer: {
    paddingBottom: '4px',
  },
};

function ZipField({ arialLabel, label, value, onChange, onPressEnter}) {
  if (!label || label.length === 0) return null;
  return (
      <div>
        <div style={styles.textContainer}>
          <p style={styles.text}>{label}</p>
        </div>
        <Input
            aria-label={arialLabel}
            onChange={onChange}
            value={value}
            style={{ width: '100%' }}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            onPressEnter={onPressEnter}
        />
      </div>
  )
}

function DateField({ arialLabel, label, value, onChange}) {
  if (!label || label.length === 0) return null;
  return (
    <div>
      <div style={styles.textContainer}>
        <p style={styles.text}>{label}</p>
      </div>
      <DatePicker
          aria-label={arialLabel}
          value={value}
          onChange={onChange}
          style={{ width: '100%' }}
      />
    </div>
  )
}

function StringField({ arialLabel, label, value, onChange, onPressEnter}) {
  if (!label || label.length === 0) return null;
  return (
      <div>
        <div style={styles.textContainer}>
          <p style={styles.text}>{label}</p>
        </div>
        <Input
            aria-label={arialLabel}
            value={value}
            onChange={onChange}
            style={{ width: '100%' }}
            onPressEnter={onPressEnter}
        />
      </div>
  )
}

function ErrorMessageField({ displayError, errorMsg, errorItems, quoteNumber}) {
  if (displayError) {
    if (errorItems && errorItems.length > 0) {
      return (
          <div className="info-banner-no-margin">
            Unable to locate {quoteNumber} for:
            <ul style={{textAlign: 'left'}}>
              {errorItems.filter(item => {
                return (item);
              }).map((item, index) => (
                    <li key={index}>{item}</li>
                ))
              }
            </ul>
            Please check the combination and try again.
          </div>
      )
    } else {
      return (
          <div className="info-banner-no-margin">{errorMsg}</div>
      )
    }
  }
  return null;
}
